import React, { useEffect, useRef, useState } from "react";
import { ORDINI_COLUMN_IDS, ORDINI_COLUMN_METADATA, ORDINI_LOCKED_COLUMN_IDS } from "./ordiniColumnMetadata";
import { normalizeColumnOrder } from "./columnOrder";
import "./colonneOrdini.css";

export default function ColonneOrdiniPanel({ preference, sortField, sortDirection }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const buttonRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };
  const query = search.trim().toLocaleLowerCase("it");
  const matches = (meta) => !query || [meta.label, ...(meta.aliases || [])]
    .some((value) => value.toLocaleLowerCase("it").includes(query));
  const mandatory = ORDINI_COLUMN_METADATA.filter((meta) => meta.locked && matches(meta));
  const showFlag = !query || "flag di selezione".includes(query) || "checkbox".includes(query);
  const orderedIds = normalizeColumnOrder(preference.order, ORDINI_COLUMN_IDS, ORDINI_LOCKED_COLUMN_IDS);
  const optionalAll = orderedIds.map((id) => ORDINI_COLUMN_METADATA.find((meta) => meta.id === id))
    .filter((meta) => !meta.locked);
  const optional = optionalAll.filter(matches);
  const move = (id, offset) => {
    const next = [...orderedIds];
    const index = next.indexOf(id);
    [next[index], next[index + offset]] = [next[index + offset], next[index]];
    preference.reorder(next);
  };
  const sorted = ORDINI_COLUMN_METADATA.find((meta) => meta.sortField === sortField);
  const statusText = {
    loading: "Caricamento colonne…",
    saving: "Salvataggio…",
    error: "Salvataggio non riuscito: scelta valida solo in questa sessione",
    loadError: "Preferenze non caricate: riprova ricaricando la pagina",
  }[preference.status];

  return (
    <div className="ordini-colonne-toolbar">
      <div className="ordini-colonne-control">
        <button ref={buttonRef} type="button" className="dim tastoInputOrizzontale br3 ordini-colonne-button"
          title="Imposta colonne" aria-label="Imposta colonne"
          aria-expanded={open} aria-controls="ordini-colonne-panel"
          onClick={() => open ? close() : setOpen(true)}>
          <span className="material-icons colScrittaDef-20" aria-hidden="true">view_column</span>
        </button>
        {open && (
          <div id="ordini-colonne-panel" className="ordini-colonne-panel"
            role="dialog" aria-label="Colonne della tabella ordini">
            <div className="ordini-colonne-panel-top">
              <strong>Colonne</strong>
              <button type="button" onClick={close} aria-label="Chiudi pannello colonne">×</button>
            </div>
            {statusText && <div className="ordini-colonne-status" role="status">{statusText}</div>}
            <label htmlFor="ordini-colonne-search">Cerca una colonna…</label>
            <input ref={searchRef} id="ordini-colonne-search" type="search"
              value={search} onChange={(event) => setSearch(event.target.value)}
              placeholder="Cerca una colonna…" />
            {(mandatory.length > 0 || showFlag) && <div className="ordini-colonne-group">
              <strong>Sempre visibili</strong>
              {showFlag && <div className="ordini-colonne-fixed">☑ Flag di selezione <span>Bloccata</span></div>}
              {mandatory.map((meta) => <div key={meta.id} className="ordini-colonne-fixed">
                ☑ {meta.label} <span>Bloccata</span>
              </div>)}
            </div>}
            <div className="ordini-colonne-group">
              <strong>Altre colonne</strong>
              <small>Riordina con le frecce o trascinando le intestazioni della tabella.</small>
              {query && <small>Le frecce spostano la colonna nell'elenco completo.</small>}
              {optional.length ? optional.map((meta) => (
                <div key={meta.id} className="ordini-colonne-choice-row">
                  <label className="ordini-colonne-choice">
                    <input type="checkbox" disabled={!preference.ready}
                      checked={preference.columns[meta.id] !== false}
                      onChange={(event) => preference.change(meta.id, event.target.checked)} />
                    <span>{meta.label}</span>
                  </label>
                  <div className="ordini-colonne-move">
                    <button type="button" aria-label={`Sposta ${meta.label} su`} title={`Sposta ${meta.label} su`}
                      disabled={!preference.ready || optionalAll[0].id === meta.id}
                      onClick={() => move(meta.id, -1)}>↑</button>
                    <button type="button" aria-label={`Sposta ${meta.label} giù`} title={`Sposta ${meta.label} giù`}
                      disabled={!preference.ready || optionalAll[optionalAll.length - 1].id === meta.id}
                      onClick={() => move(meta.id, 1)}>↓</button>
                  </div>
                </div>
              )) : <span>Nessuna colonna trovata.</span>}
            </div>
            <button type="button" className="ordini-colonne-reset" disabled={!preference.ready}
              onClick={preference.reset}>Ripristina colonne predefinite</button>
          </div>
        )}
      </div>
      {sorted && sortDirection && preference.columns[sorted.id] === false && <span className="ordini-colonne-sort">
        Ordinamento: {sorted.label} {sortDirection === "asc" ? "↑" : "↓"}
      </span>}
    </div>
  );
}
